import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, AlertCircle, RefreshCw, Home, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { cn } from '../../lib/utils';

// ---------------------------------------------------------------------------
// Fallback UIs
// ---------------------------------------------------------------------------

/** Default full error fallback with retry, home, refresh, and expandable details */
interface DefaultErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  onRetry: () => void;
}

const DefaultErrorFallback: React.FC<DefaultErrorFallbackProps> = ({
  error,
  errorInfo,
  showDetails,
  onRetry,
}) => {
  const [showErrorDetails, setShowErrorDetails] = React.useState(false);

  const handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-[400px] flex items-center justify-center p-6">
      <div className="max-w-md w-full space-y-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle className="text-lg">Something went wrong</AlertTitle>
          <AlertDescription className="mt-2">
            We're sorry, but something unexpected happened. Please try again or contact support if the problem persists.
          </AlertDescription>
        </Alert>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={onRetry} className="flex-1">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
          <Button variant="outline" onClick={handleGoHome} className="flex-1">
            <Home className="h-4 w-4 mr-2" />
            Go to Dashboard
          </Button>
        </div>

        <Button
          variant="ghost"
          onClick={handleRefreshPage}
          className="w-full text-muted-foreground"
          size="sm"
        >
          Refresh Page
        </Button>

        {showDetails && error && (
          <div className="mt-4">
            <button
              onClick={() => setShowErrorDetails(prev => !prev)}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {showErrorDetails ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              {showErrorDetails ? 'Hide' : 'Show'} technical details
            </button>

            {showErrorDetails && (
              <div className="mt-2 p-3 bg-muted rounded-md overflow-auto max-h-48">
                <p className="text-xs font-mono text-destructive mb-2">
                  {error.name}: {error.message}
                </p>
                {errorInfo && (
                  <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                    {errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

/** Full-page error fallback for route-level crashes */
interface PageErrorFallbackProps {
  title?: string;
  message?: string;
}

const PageErrorFallback: React.FC<PageErrorFallbackProps> = ({
  title = 'Page Error',
  message = 'This page encountered an unexpected error. Please try again.',
}) => {
  const handleGoBack = () => {
    window.history.back();
  };

  const handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center p-6">
      <div className="text-center max-w-md space-y-6">
        {/* Error Icon */}
        <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>

        {/* Error Message */}
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
          <p className="text-muted-foreground">{message}</p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={handleRefresh} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh Page
          </Button>
          <Button variant="outline" onClick={handleGoBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
          <Button variant="ghost" onClick={handleGoHome} className="gap-2">
            <Home className="h-4 w-4" />
            Dashboard
          </Button>
        </div>

        {/* Help Text */}
        <p className="text-xs text-muted-foreground">
          If this problem persists, please contact support.
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

/** Compact error fallback for dashboard widgets and smaller components */
interface WidgetErrorFallbackProps {
  title?: string;
  compact?: boolean;
  onRetry?: () => void;
  className?: string;
}

const WidgetErrorFallback: React.FC<WidgetErrorFallbackProps> = ({
  title = 'Widget Error',
  compact = false,
  onRetry,
  className,
}) => {
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  if (compact) {
    return (
      <div className={cn(
        "flex items-center justify-center gap-2 p-4 text-muted-foreground",
        className
      )}>
        <AlertCircle className="h-4 w-4 text-destructive" />
        <span className="text-sm">Failed to load</span>
        <Button variant="ghost" size="sm" onClick={handleRetry} className="h-6 px-2">
          <RefreshCw className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className={cn(
      "flex flex-col items-center justify-center p-6 space-y-3 text-center",
      className
    )}>
      <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
        <AlertCircle className="h-5 w-5 text-destructive" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">
          Something went wrong loading this component
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={handleRetry} className="gap-1">
        <RefreshCw className="h-3 w-3" />
        Retry
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Core ErrorBoundary class component
// ---------------------------------------------------------------------------

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary - Catches JavaScript errors in child component tree
 *
 * Features:
 * - Displays user-friendly error message
 * - Option to show technical details for debugging
 * - Retry and navigation options
 * - Logs errors to console and Sentry (if configured)
 *
 * Usage (standalone):
 *   <ErrorBoundary>
 *     <MyComponent />
 *   </ErrorBoundary>
 *
 * Usage (with custom fallback):
 *   <ErrorBoundary fallback={<div>Oops!</div>}>
 *     <MyComponent />
 *   </ErrorBoundary>
 *
 * For convenience wrappers see PageErrorBoundary and WidgetErrorBoundary below.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);

    // Update state with error info
    this.setState({ errorInfo });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Send to Sentry for error tracking (if configured)
    if (process.env['REACT_APP_SENTRY_DSN']) {
      // Dynamic import to avoid compile-time dependency
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      try {
        const Sentry = require('@sentry/react');
        Sentry.withScope((scope: { setExtra: (key: string, value: unknown) => void; setTag: (key: string, value: string) => void }) => {
          scope.setExtra('componentStack', errorInfo.componentStack);
          scope.setTag('errorBoundary', 'true');
          Sentry.captureException(error);
        });
      } catch {
        // Sentry not available - skip error reporting
      }
    }
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  override render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, showDetails = true } = this.props;

    if (hasError) {
      // If custom fallback provided, use it
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <DefaultErrorFallback
          error={error}
          errorInfo={errorInfo}
          showDetails={showDetails}
          onRetry={this.handleRetry}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;

// ---------------------------------------------------------------------------
// Convenience wrappers
// ---------------------------------------------------------------------------

interface PageErrorBoundaryProps {
  children: ReactNode;
  pageName?: string;
}

/**
 * PageErrorBoundary - Wraps entire pages to catch rendering errors
 *
 * Usage:
 * <PageErrorBoundary pageName="Dashboard">
 *   <Dashboard />
 * </PageErrorBoundary>
 */
const PageErrorBoundary: React.FC<PageErrorBoundaryProps> = ({
  children,
  pageName = 'Page',
}) => {
  const handleError = (error: Error) => {
    // Log page-level error with context
    console.error(`[PageErrorBoundary] Error in ${pageName}:`, error);
    // TODO: Send to error tracking service with page context
  };

  return (
    <ErrorBoundary
      onError={handleError}
      fallback={
        <PageErrorFallback
          title={`${pageName} Error`}
          message={`We couldn't load this page. Please try refreshing or go back.`}
        />
      }
    >
      {children}
    </ErrorBoundary>
  );
};

// ---------------------------------------------------------------------------

interface WidgetErrorBoundaryProps {
  children: ReactNode;
  widgetName?: string;
  compact?: boolean;
  className?: string;
  onRetry?: () => void;
}

/**
 * WidgetErrorBoundary - Wraps dashboard widgets and smaller components
 *
 * Usage:
 * <WidgetErrorBoundary widgetName="Revenue Chart">
 *   <RevenueChart />
 * </WidgetErrorBoundary>
 *
 * Compact mode for smaller widgets:
 * <WidgetErrorBoundary widgetName="Stats" compact>
 *   <StatsWidget />
 * </WidgetErrorBoundary>
 */
const WidgetErrorBoundary: React.FC<WidgetErrorBoundaryProps> = ({
  children,
  widgetName = 'Widget',
  compact = false,
  className,
  onRetry,
}) => {
  const handleError = (error: Error) => {
    console.error(`[WidgetErrorBoundary] Error in ${widgetName}:`, error);
  };

  return (
    <ErrorBoundary
      onError={handleError}
      showDetails={false}
      fallback={
        <WidgetErrorFallback
          title={`${widgetName} Error`}
          compact={compact}
          onRetry={onRetry}
          className={className}
        />
      }
    >
      {children}
    </ErrorBoundary>
  );
};

// ---------------------------------------------------------------------------
// Named exports for all public components
// ---------------------------------------------------------------------------

export { ErrorBoundary, PageErrorBoundary, PageErrorFallback, WidgetErrorBoundary, WidgetErrorFallback };
